/* eslint-disable @typescript-eslint/no-unused-vars */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { createDraftAPI, createProjectAPI, getDraftsAPI, getProjectsAPI, removeProjectAPI,removeDraftProjectAPI } from '../api/project';
import { RootState, AppThunk } from '../store';

export interface ProjectState {
  fetchingProjects: boolean,
  fetchingDrafts: boolean,
  fetchingError: boolean,
  hasFan: boolean,
  projectState: "new" | "unsaved" | "draft" | "saved",
  projectData: {
    projectDetails: any,
    fanDetails: any,
    gaDetails: any,
  },
  fanData: any,
  projectsList?: any[],
  draftsList?: any[],
  pressureCurveChoosen: "total"|"static",
}

const initialState: ProjectState = {
  fetchingProjects: false,
  fetchingDrafts: false,
  fetchingError: true,
  hasFan: false,
  projectState: "new",
  projectData: {
    projectDetails: undefined,
    fanDetails: undefined,
    gaDetails: undefined
  },
  fanData: undefined,
  projectsList: undefined,
  draftsList: undefined,
  pressureCurveChoosen: "total",
}

export const createNewDraft = createAsyncThunk(
  'project/createNewDraft',
  async (data: any) => {
    const response = await createDraftAPI(data);
    return response.response;
  }
);

export const createNewProject = createAsyncThunk(
  'project/createNewProject',
  async (data: any) => {
    const response = await createProjectAPI(data);
    return response.response;
  }
);

export const removeProject = createAsyncThunk(
  'project/remove',
  async (data: any) => {
    const response = await removeProjectAPI(data);
    return response.response;
  }
);

export const removeDraftProject = createAsyncThunk(
  'draftproject/remove',
  async (data: any) => {
    const response = await removeDraftProjectAPI(data);
    return response.response;
  }
);

export const getDrafts = createAsyncThunk(
  'project/getDrafts',
  async () => {
    const response = await getDraftsAPI();
    return response.response;
  }
);

export const getProjects = createAsyncThunk(
  'project/getProjects',
  async () => {
    const response = await getProjectsAPI();
    return response.response;
  }
);

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    resetProject: (state) => {
      state.hasFan = false;
      state.projectState = 'new';
      state.fanData = undefined;
      state.projectData = initialState.projectData;
    },
     setPressureCurveMode(state, action: PayloadAction<"total" | "static">) { // to access the value of choosen curve tyoe in datasheet section
      state.pressureCurveChoosen = action.payload;
    },
    updateProjectDetails: (state, action: PayloadAction<any>) => {
      state.projectData.projectDetails = action.payload;
    },
    updateProjectState: (state, action: PayloadAction<any>) => {
      state.projectState = action.payload;
    },
    updateFanDetails: (state, action: PayloadAction<any>) => {
      state.projectData.fanDetails = action.payload;
    },
    updateGADetails: (state, action: PayloadAction<any>) => {
      state.projectData.gaDetails = action.payload;
    },
    selectFan: (state, action: PayloadAction<any>) => {
      state.fanData = action.payload;
      state.hasFan = true;
    },
    changeOctaveBandPoint: (state, action: PayloadAction<any>) => {
      const { index, value } = action.payload
      state.fanData.octaveBand.dB[index] = value;
    },
    updatePerformanceCurvePoint: (state, action: PayloadAction<any>) => {
      const { coordinates, pressure, type } = action.payload
      if (type === "total") {
        state.fanData.performance_curves[coordinates.flowIndex].plotPoints[coordinates.angleIndex].total_pressure = pressure;

      } else {
        state.fanData.performance_curves[coordinates.flowIndex].plotPoints[coordinates.angleIndex].static_pressure = pressure;
      }
    },
    changeTorqueCurvePoint: (state, action: PayloadAction<any>) => {
      const { index, value } = action.payload
      state.fanData.torque.points[index] = value;
    },
    changeRPM: (state, action: PayloadAction<any>) => {
      state.fanData.octaveBand.rpm = action.payload.value;
    },
    resetFanStore: (state) => {
      state = initialState;
    },
    updatePerformanceAirflow: (state, action: PayloadAction<any>) => {
      const { flow, index} = action.payload
      state.fanData.performance_curves[index].flow= flow;
    },
    updatePerformanceAngle: (state, action: PayloadAction<any>) => {
      const { newAngle,index,oldAngle } = action.payload
      const data = state.fanData.performance_curves;
      for (let i = 0; i < data.length; i++) {
        const  plotPoints = data[i].plotPoints[index];
            if (oldAngle === plotPoints.graphAngle) {
              state.fanData.performance_curves[i].plotPoints[index].graphAngle = newAngle;
            }
        }

    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createNewDraft.pending, (state) => {
        state.fetchingDrafts = true;
        state.fetchingError = false;
      })
      .addCase(createNewDraft.fulfilled, (state) => {
        state.fetchingDrafts = false;
        state.fetchingError = false;
        state.projectState = 'draft';
      })
      .addCase(createNewDraft.rejected, (state) => {
        state.fetchingDrafts = false;
        state.fetchingError = true;
      })

      .addCase(createNewProject.pending, (state) => {
        state.fetchingProjects = true;
        state.fetchingError = false;
      })
      .addCase(createNewProject.fulfilled, (state) => {
        state.fetchingProjects = false;
        state.fetchingError = false;
        state = initialState;
      })
      .addCase(createNewProject.rejected, (state) => {
        state.fetchingError = true;
        state.fetchingProjects = false;
      })

      .addCase(removeProject.pending, (state) => {
        state.fetchingProjects = true;
        state.fetchingError = false;
      })
      .addCase(removeProject.fulfilled, (state) => {
        state.fetchingProjects = false;
        state.fetchingError = false;
        state = initialState;
      })
      .addCase(removeProject.rejected, (state) => {
        state.fetchingError = true;
        state.fetchingProjects = false;
      })

      .addCase(removeDraftProject.pending, (state) => {
        state.fetchingDrafts = true;
        state.fetchingError = false;
      })
      .addCase(removeDraftProject.fulfilled, (state) => {
        state.fetchingDrafts = false;
        state.fetchingError = false;
        state = initialState;
      })
      .addCase(removeDraftProject.rejected, (state) => {
        state.fetchingError = true;
        state.fetchingDrafts = false;
      })

      

      .addCase(getDrafts.pending, (state) => {
        state.fetchingDrafts = true;
        state.fetchingError = false;
      })
      .addCase(getDrafts.fulfilled, (state, action) => {
        state.fetchingDrafts = false;
        state.fetchingError = false;
        state.draftsList = action.payload.data
      })
      .addCase(getDrafts.rejected, (state) => {
        state.fetchingDrafts = false;
        state.fetchingError = true;
      })

      .addCase(getProjects.pending, (state) => {
        state.fetchingProjects = true;
        state.fetchingError = false;
      })
      .addCase(getProjects.fulfilled, (state, action) => {
        state.fetchingProjects = false;
        state.fetchingError = false;
        state.projectsList = action.payload.data
      })
      .addCase(getProjects.rejected, (state) => {
        state.fetchingProjects = false;
        state.fetchingError = true;
      });
  }
})


export const changePerformanceCurveAirflow = (payload: any): AppThunk => (
  dispatch,
  getState
) => {
   dispatch(updatePerformanceAirflow({flow: payload.flow,index:payload.index }));
};

export const changePerformanceCurveAngle = (payload: any): AppThunk => (
  dispatch,
  getState
) => {
  console.log(payload)
  if(payload.newAngle){
    dispatch(updatePerformanceAngle({newAngle: payload.newAngle,index:payload.index,oldAngle: payload.oldAngle}));
  }
};


export const changePerformanceCurvePoint = (payload: any): AppThunk => (
  dispatch,
  getState
) => {

  var currentPerformanceCurve = getPerformanceCurve(getState());

  var coordinates: any = {
    flowIndex: null,
    angleIndex: null
  }

  for (let i = 0; i < currentPerformanceCurve.length; i++) {

    if (payload.flow === currentPerformanceCurve[i].flow) {

      for (let j = 0; j < currentPerformanceCurve[i].plotPoints.length; j++) {

        if (payload.angle === currentPerformanceCurve[i].plotPoints[j].graphAngle) {

          coordinates.flowIndex = i;
          coordinates.angleIndex = j;
          break;
          // tempPFC[i].plotPoints[j].pressure = payload.pressure;

        }

      }

    }

  }

  dispatch(updatePerformanceCurvePoint({ coordinates, pressure: payload.pressure, type: payload.type }));

};

export const { resetProject, updateProjectDetails, updateFanDetails, updateGADetails, selectFan, updatePerformanceCurvePoint,changeOctaveBandPoint, changeTorqueCurvePoint, changeRPM, resetFanStore, updateProjectState ,updatePerformanceAirflow , updatePerformanceAngle,setPressureCurveMode } = projectSlice.actions;

export const getPressureCurveMode = (state: RootState) => state.project.pressureCurveChoosen;
export const getProjectState = (state: RootState) => state.project.projectState;
export const getHasFan = (state: RootState) => state.project.hasFan;

export const getProjectDetails = (state: RootState) => state.project.projectData.projectDetails;
export const getFanDetails = (state: RootState) => state.project.projectData.fanDetails;
export const getGADetails = (state: RootState) => state.project.projectData.gaDetails;

export const getPerformanceCurve = (state: RootState) => state.project.fanData.performance_curves;
export const getFanData = (state: RootState) => state.project.fanData;

export const getFetching = (state: RootState) => state.project.fetchingProjects;
export const getFetchingError = (state: RootState) => state.project.fetchingError;


export default projectSlice.reducer;
